import { MODULE_TO_TABLE } from "../constants";
import type {
  DailyLog,
  GroomingItem,
  LearningItem,
  NutritionItem,
  Profile,
  QueueItem,
  RitualSnapshot,
  SyncRecord,
  TableName,
  WardrobeItem,
} from "../types";
import { isoNow } from "./date";
import { newId } from "./id";
import { supabase } from "./supabase";

type ItemTable =
  | "nutrition_items"
  | "grooming_items"
  | "wardrobe_items"
  | "learning_items";

const ITEM_TABLES: ItemTable[] = [
  "nutrition_items",
  "grooming_items",
  "wardrobe_items",
  "learning_items",
];

const COLLECTION_FOR_TABLE = {
  nutrition_items: "nutrition",
  grooming_items: "grooming",
  wardrobe_items: "wardrobe",
  learning_items: "learning",
} as const;

function timeValue(value: string | null | undefined) {
  return value ? Date.parse(value) || 0 : 0;
}

export function recordClock(record: Pick<SyncRecord, "created_at" | "updated_at" | "deleted_at">) {
  return Math.max(
    timeValue(record.created_at),
    timeValue(record.updated_at),
    timeValue(record.deleted_at),
  );
}

function deletionClock(record: Pick<SyncRecord, "deleted_at">) {
  return timeValue(record.deleted_at);
}

export function chooseRecord<T extends SyncRecord>(local: T, remote: T): T {
  const localDelete = deletionClock(local);
  const remoteDelete = deletionClock(remote);

  if (localDelete || remoteDelete) {
    if (remoteDelete > localDelete) return remote;
    if (localDelete > remoteDelete) return local;
  }

  return recordClock(remote) >= recordClock(local) ? remote : local;
}

export function mergeRecords<T extends SyncRecord>(local: T[], remote: T[]) {
  const byId = new Map<string, T>();
  for (const item of local) byId.set(item.id, item);

  for (const item of remote) {
    const existing = byId.get(item.id);
    byId.set(item.id, existing ? chooseRecord(existing, item) : item);
  }

  return [...byId.values()].sort((a, b) => recordClock(b) - recordClock(a));
}

function logKey(log: Pick<DailyLog, "log_date" | "category" | "item_id" | "occurrence_id">) {
  return `${log.log_date}_${log.category}_${log.item_id}_${log.occurrence_id || "default"}`;
}

export function mergeLogs(local: DailyLog[], remote: DailyLog[]) {
  const byKey = new Map<string, DailyLog>();
  for (const log of [...local, ...remote]) {
    const key = logKey(log);
    const existing = byKey.get(key);
    if (!existing || timeValue(log.created_at) >= timeValue(existing.created_at)) {
      byKey.set(key, log);
    }
  }
  return [...byKey.values()].sort((a, b) => timeValue(b.created_at) - timeValue(a.created_at));
}

function normalizePayload(table: TableName, payload: Record<string, unknown>, userId: string) {
  if (table === "profiles") {
    return { ...payload, id: userId };
  }

  if (table === "daily_logs") {
    return {
      ...payload,
      id: `${userId}_${String(payload.log_date)}_${String(payload.category)}_${String(payload.item_id)}_${String(payload.occurrence_id || "default")}`,
      user_id: userId,
      occurrence_id: String(payload.occurrence_id || "default"),
    };
  }

  return { ...payload, user_id: userId };
}

function queueItem(table: TableName, payload: Record<string, unknown>): QueueItem {
  return {
    id: newId(),
    table,
    op: "upsert",
    payload,
    created_at: isoNow(),
  };
}

export function buildFullUploadQueue(snapshot: RitualSnapshot): QueueItem[] {
  const rows: QueueItem[] = [queueItem("profiles", snapshot.profile as unknown as Record<string, unknown>)];

  for (const item of snapshot.nutrition) {
    rows.push(queueItem(MODULE_TO_TABLE.nutrition, item as unknown as Record<string, unknown>));
  }
  for (const item of snapshot.grooming) {
    rows.push(queueItem(MODULE_TO_TABLE.grooming, item as unknown as Record<string, unknown>));
  }
  for (const item of snapshot.wardrobe) {
    rows.push(queueItem(MODULE_TO_TABLE.wardrobe, item as unknown as Record<string, unknown>));
  }
  for (const item of snapshot.learning) {
    rows.push(queueItem(MODULE_TO_TABLE.learning, item as unknown as Record<string, unknown>));
  }
  for (const log of snapshot.logs) {
    rows.push(queueItem("daily_logs", log as unknown as Record<string, unknown>));
  }

  return rows;
}

async function upsertQueue(queue: QueueItem[], userId: string) {
  if (!supabase) throw new Error("Supabase is not configured.");

  for (const item of queue) {
    const payload = normalizePayload(item.table, item.payload, userId);
    const onConflict =
      item.table === "daily_logs" ? "user_id,log_date,category,item_id,occurrence_id" : "id";
    const { error } = await (supabase as never as { from: (table: string) => { upsert: (payload: unknown, options: { onConflict: string }) => Promise<{ error: Error | null }> } })
      .from(item.table)
      .upsert(payload, { onConflict });

    if (error) throw error;
  }
}

async function fetchTable<T>(table: TableName, userId: string): Promise<T[]> {
  if (!supabase) throw new Error("Supabase is not configured.");

  const query =
    table === "profiles"
      ? supabase.from(table).select("*").eq("id", userId)
      : supabase.from(table).select("*").eq("user_id", userId);

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as T[];
}

function normalizeProfile(local: Profile, remote: Profile | undefined, userId: string): Profile {
  const now = isoNow();
  if (remote && timeValue(remote.updated_at) >= timeValue(local.updated_at)) return remote;

  return {
    ...local,
    id: userId,
    created_at: local.created_at || now,
    updated_at: local.updated_at || now,
  };
}

export async function syncWithSupabase(
  snapshot: RitualSnapshot,
  queue: QueueItem[],
  userId: string,
): Promise<RitualSnapshot> {
  if (!supabase) throw new Error("Supabase is not configured.");

  await upsertQueue(queue, userId);

  const [profiles, nutrition, grooming, wardrobe, learning, logs] = await Promise.all([
    fetchTable<Profile>("profiles", userId),
    fetchTable<NutritionItem>("nutrition_items", userId),
    fetchTable<GroomingItem>("grooming_items", userId),
    fetchTable<WardrobeItem>("wardrobe_items", userId),
    fetchTable<LearningItem>("learning_items", userId),
    fetchTable<DailyLog>("daily_logs", userId),
  ]);

  return {
    profile: normalizeProfile(snapshot.profile, profiles[0], userId),
    nutrition: mergeRecords(snapshot.nutrition, nutrition),
    grooming: mergeRecords(snapshot.grooming, grooming),
    wardrobe: mergeRecords(snapshot.wardrobe, wardrobe),
    learning: mergeRecords(snapshot.learning, learning),
    logs: mergeLogs(snapshot.logs, logs),
    last_synced_at: isoNow(),
  };
}

export function collectionForTable(table: ItemTable) {
  return COLLECTION_FOR_TABLE[table];
}

export { ITEM_TABLES };
